import { useEffect, useState, useMemo, useCallback } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import useDocDownloadError from "../../hooks/useDocDownloadError";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getInvestorView } from "../../redux/slices/investorSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import InvestorTable from "./Components/InvestorTable";
import { capitalizeEntireWord, downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { INV_STATUS_DISPLAY_NAMES, STATUS } from "../../constants/constants";
import { INV_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import "./Investor.scss";
import { count } from "../../redux/slices/dealsSlice";

const KYC_STATUS_GROUP = [
  STATUS.KYC_PENDING.ID,
  STATUS.KYC_VERIFIED.ID,
  STATUS.KYC_REVIEW.ID,
];

const buildStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

const Investor = () => {
  const dispatch = useDispatch();
  const { investor } = useSelector((state) => state?.investor);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const docLoading = useDocDownloadError();
  const { CountData } = useSelector((state) => state?.deals);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [columnControl, setColumnControl] = useState(null);
  const { searchText, setSearchText, filteredData } = useSearchableTable(investor);
  const { allowedStatusIds } = useUserPrivileges(INV_STATUS_PRIVILEGE_MAP);

const DEFAULT_INV_STATUSES = [
  STATUS.PENDING.ID,
  STATUS.ACTIVE.ID,
];

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("bus"));
      if (res.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_INV_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        setSelectedStatus(
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [],
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  useEffect(() => {
    if (selectedStatus) {
      dispatch(getInvestorView(selectedStatus));
    }
  }, [dispatch, selectedStatus]);

  useEffect(() => {
    dispatch(count("INVESTOR"));
  }, [dispatch]);

  const totalKycCount = useMemo(
    () => CountData
      ?.filter((c) => KYC_STATUS_GROUP.includes(c.statusId))
      ?.reduce((sum, item) => sum + item.count, 0) || 0,
    [CountData],
  );

  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);

  const filteredStatusList = useMemo(
    () => statusList?.filter((status) => allowedStatusIds.includes(status.statusId)) ?? [],
    [statusList, allowedStatusIds],
  );

  const statusListWithCounts = useMemo(
    () => filteredStatusList.map((item) => {
      if (item.statusId === STATUS.PENDING.ID) {
        return { ...item, count: totalKycCount };
      }
      return {
        ...item,
        count: CountData?.find((c) => c.statusId === item.statusId)?.count || 0,
      };
    }),
    [filteredStatusList, totalKycCount, CountData],
  );

  const handleDownload = useCallback(
    (type) => downloadPageHandle({ dispatch, type, screenName: "investor", statusId: selectedStatus }),
    [dispatch, selectedStatus],
  );

  const handleDownloadPdf = useCallback(() => handleDownload("pdf"), [handleDownload]);
  const handleDownloadExcel = useCallback(() => handleDownload("excel"), [handleDownload]);

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        selectedStatus,
        statusDisplayNames: INV_STATUS_DISPLAY_NAMES,
        statusListWithCounts,
        defaultTitle: "Investor",
      }),
    [selectedStatus, statusListWithCounts],
  );

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={setSearchText}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={INV_STATUS_DISPLAY_NAMES}
        isDownloadRequired={true}
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
        columnControl={columnControl}
      />
      <InvestorTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        onColumnControlChange={setColumnControl}
      />
    </CustomCard>
  );
};

export default Investor;